import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  GMAIL_ACCOUNT_LIST_QUERY_KEY,
  GMAIL_ACCOUNT_QUERY_KEY,
} from "../api/useGmailAccount";
import { GMAIL_INBOX_QUERY_KEY } from "../api/useGmailInbox";
import {
  GMAIL_OAUTH_CHANNEL,
  type GmailOAuthBroadcast,
} from "../api/useGmailMutations";

/**
 * The server sends a Google consent popup back to Xale with
 * ?gmail=connected|failed (connect, lands on Settings → Apps) or
 * ?gmail=inbox|inbox_failed (inbox access, lands on /gmail).
 */
function readOAuthReturn(): GmailOAuthBroadcast | null {
  const sp = new URLSearchParams(window.location.search);
  const reason = sp.get("reason") ?? undefined;
  switch (sp.get("gmail")) {
    case "connected":
      return { kind: "connect", ok: true };
    case "failed":
      return { kind: "connect", ok: false, reason };
    case "inbox":
      return { kind: "inbox", ok: true };
    case "inbox_failed":
      return { kind: "inbox", ok: false, reason };
    default:
      return null;
  }
}

/** Google's failure reasons, in words a user can act on. */
function resultText(r: GmailOAuthBroadcast): string {
  if (r.ok) return r.kind === "inbox" ? "Inbox connected" : "Gmail connected";
  if (r.reason === "access_denied") {
    return r.kind === "inbox"
      ? "Inbox access was cancelled on Google's screen."
      : "Gmail connection was cancelled on Google's screen.";
  }
  if (r.reason === "inbox_not_granted") {
    return "Tick “Read your email” on Google's screen to see your inbox here.";
  }
  return (
    r.reason ||
    (r.kind === "inbox"
      ? "Couldn't turn on inbox access. Try again."
      : "Couldn't connect Gmail. Try again.")
  );
}

/**
 * Mounted once at app root. In the consent popup: report the result over a
 * BroadcastChannel and close. In the window that opened it: toast the result
 * and refresh everything Gmail-related.
 */
export default function GmailOAuthReturn() {
  const qc = useQueryClient();

  useEffect(() => {
    const announce = (r: GmailOAuthBroadcast) => {
      if (r.ok) toast.success(resultText(r));
      else toast.error(resultText(r));
      qc.invalidateQueries({ queryKey: GMAIL_ACCOUNT_QUERY_KEY });
      qc.invalidateQueries({ queryKey: GMAIL_ACCOUNT_LIST_QUERY_KEY });
      qc.invalidateQueries({ queryKey: [GMAIL_INBOX_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: ["lead-sources"] });
    };

    const landed = readOAuthReturn();
    if (landed) {
      if (typeof BroadcastChannel !== "undefined") {
        const out = new BroadcastChannel(GMAIL_OAUTH_CHANNEL);
        out.postMessage(landed);
        out.close();
      }
      // Google can sever the opener and clear the name; then this may still
      // be the popup, so it reports here as well and stays open.
      if (window.opener || window.name === "gmail-oauth") {
        window.close();
      } else {
        announce(landed);
        const url = new URL(window.location.href);
        url.searchParams.delete("gmail");
        url.searchParams.delete("reason");
        window.history.replaceState({}, "", url.toString());
      }
    }

    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(GMAIL_OAUTH_CHANNEL);
    channel.onmessage = (e: MessageEvent<GmailOAuthBroadcast>) => {
      if (e.data) announce(e.data);
    };
    return () => channel.close();
  }, [qc]);

  return null;
}
